import { useState } from "react";

import { Button } from "@components/Button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@components/Dialog";
import { Image } from "@components/Image";
import { Stack } from "@components/Stack";
import { Textarea } from "@components/Textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/Tooltip";
import { Typography } from "@components/Typography";

import { useBlockUserByIdMutation } from "@storage/queries/blacklist";

import { cn } from "@lib/utils";

import { ICON_PATHS } from "@configs/icons";

enum Term {
	HOUR = "hour",
	DAY = "day",
	WEEK = "week",
	PERMANENT = "permanent",
}
const termDropdownOption = "z-dropdown cursor-pointer rounded-sm p-3 hover:bg-ground-hover";

interface BlockUserDialogProps {
	userId: string;
	username: string;
	onClose: () => void;
}

export const BlockUserDialog = ({ userId, username, onClose }: BlockUserDialogProps) => {
	const [reason, setReason] = useState("");
	const [termLabel, setTermLabel] = useState("1 hour");
	const [termValue, setTermValue] = useState<string>(Term.HOUR);
	const [isTermMenuOpen, setIsTermMenuOpen] = useState(false);

	const blockUserByIdMutation = useBlockUserByIdMutation();

	const closeBlockDialog = () => {
		setIsTermMenuOpen(false);
		setTermLabel("1 hour");
		onClose();
	};

	const handleBlockUser = async () => {
		blockUserByIdMutation.mutate({ userId: userId, reason, term: termValue });
		setReason("");
		setTermValue(Term.HOUR);
		closeBlockDialog();
	};

	const selectBlockTerm = (e: React.MouseEvent<HTMLDivElement>, value: string) => {
		setTermLabel(e.currentTarget.innerText);
		setTermValue(value);
	};

	const chevronClass = isTermMenuOpen
		? "duration-150 -translate-y-0.5 rotate-180 [filter:brightness(0)_saturate(100%)_invert(59%)_sepia(7%)_saturate(394%)_hue-rotate(232deg)_brightness(92%)_contrast(87%)]"
		: "duration-150 -translate-y-px transition-transform";

	return (
		<Dialog open onOpenChange={onClose}>
			<DialogContent className="max-w-116.25">
				<DialogHeader>
					<DialogTitle className="max-w-50">Are you sure you want to Ban {username}?</DialogTitle>
				</DialogHeader>
				<Tooltip>
					<TooltipTrigger className="flex h-6 items-center gap-2">
						<Typography color="msg_time" padding="none" size="sm">
							How long would you like to keep the user suspended?
						</Typography>
						<Image src={ICON_PATHS.INFO} alt="info" />
					</TooltipTrigger>
					<TooltipContent className="max-w-50">
						<Typography size="xs" padding="none">
							Removing the user from the Blacklist is possible at any time
						</Typography>
					</TooltipContent>
				</Tooltip>

				<div className="relative mt-2 text-sm" onClick={() => setIsTermMenuOpen((prev) => !prev)}>
					<Stack direction="row" spacing={2} align="center" className="cursor-pointer">
						<Typography size="sm" weight="bold" color={isTermMenuOpen ? "grey-tertiary" : "text"} padding="none">
							{termLabel}
						</Typography>
						<Image className={chevronClass} src={ICON_PATHS.ARROW_DOWN} alt="chevron" />
					</Stack>

					<div
						className={
							isTermMenuOpen
								? `absolute top-5.75 left-0 z-dropdown flex w-56 max-w-49 flex-col justify-center rounded-lg bg-block
									py-1 shadow-xl`
								: "hidden"
						}
					>
						<div
							onClick={(e) => selectBlockTerm(e, Term.HOUR)}
							className={cn(termDropdownOption, termLabel === "1 hour" ? "bg-primary hover:bg-primary" : "")}
						>
							<Typography size="sm" padding="none">
								1 {Term.HOUR}
							</Typography>
						</div>
						<div
							onClick={(e) => selectBlockTerm(e, Term.DAY)}
							className={cn(termDropdownOption, termLabel === "1 day" ? "bg-primary hover:bg-primary" : "")}
						>
							<Typography size="sm" padding="none">
								1 {Term.DAY}
							</Typography>
						</div>
						<div
							onClick={(e) => selectBlockTerm(e, Term.WEEK)}
							className={cn(termDropdownOption, termLabel === "1 week" ? "bg-primary hover:bg-primary" : "")}
						>
							<Typography size="sm" padding="none">
								1 {Term.WEEK}
							</Typography>
						</div>
						<div
							onClick={(e) => selectBlockTerm(e, Term.PERMANENT)}
							className={cn(termDropdownOption, termLabel === "permanent" ? "bg-primary hover:bg-primary" : "")}
						>
							<Typography size="sm" padding="none">
								{Term.PERMANENT}
							</Typography>
						</div>
					</div>
				</div>

				<Textarea
					id="ban-reason-textarea"
					className="mt-2 rounded-lg border-secondary text-white placeholder:text-sm placeholder:text-grey-tertiary"
					value={reason}
					rows={3}
					showCount
					maxLength={100}
					placeholder="Leave a comment, so you have a record of the reason behind your action. You can access this information later in the Blacklist."
					onChange={(e) => setReason(e.target.value)}
				/>

				<DialogFooter>
					<Button type="secondary" onClick={closeBlockDialog} wide>
						<Typography weight="bold" size="sm" padding="none">
							Cancel
						</Typography>
					</Button>
					<Button type="primary" onClick={handleBlockUser} wide>
						<Typography weight="bold" size="sm" padding="none">
							Ban
						</Typography>
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
